import LetsEncryptConfigurationRepository from "client/repositories/letsEncryptConfigurationRepository";
import AuthenticationRepository from "./authentication/authenticationRepository";
import Client from "./client";
import SettingsRepository from "./configuration/settingsRepository";
import AccountRepository from "./repositories/accountRepository";
import ActionTemplateRepository from "./repositories/actionTemplateRepository";
import ArtifactRepository from "./repositories/artifactRepository";
import BackupRepository from "./repositories/backupRepository";
import BuiltInRepositoryConfigurationRepository from "./repositories/builtInRepositoryConfigurationRepository";
import CertificateConfigurationRepository from "./repositories/certificateConfigurationRepository";
import CertificateRepository from "./repositories/certificateRepository";
import ChannelRepository from "./repositories/channelRepository";
import CloudTemplateRepository from "./repositories/cloudTemplateRepository";
import CommunityActionTemplateRepository from "./repositories/communityActionTemplateRepository";
import DashboardRepository from "./repositories/dashboardRepository";
import DashboardConfigurationRepository from "./repositories/dashoboardConfigurationRepository";
import DefectRepository from "./repositories/defectRepository";
import DeploymentProcessRepository from "./repositories/deploymentProcessRepository";
import DeploymentRepository from "./repositories/deploymentRepository";
import EnvironmentRepository from "./repositories/environmentRepository";
import EventRepository from "./repositories/eventRepository";
import ExternalSecurityGroupProviderRepository from "./repositories/externalSecurityGroupProviderRepository";
import ExternalSecurityGroupRepository from "./repositories/externalSecurityGroupRepository";
import ExternalUsersRepository from "./repositories/externalUsersRepository";
import FeaturesConfigurationRepository from "./repositories/featuresConfigurationRepository";
import FeedRepository from "./repositories/feedRepository";
import InterruptionRepository from "./repositories/interruptionRepository";
import LibraryVariableRepository from "./repositories/libraryVariableRepository";
import LicenseRepository from "./repositories/licenseRepository";
import LifecycleRepository from "./repositories/lifecycleRepository";
import MachinePolicyRepository from "./repositories/machinePolicyRepository";
import MachineRepository from "./repositories/machineRepository";
import MachineRoleRepository from "./repositories/machineRoleRepository";
import MaintenanceConfigurationRepository from "./repositories/maintenanceConfigurationRepository";
import OctopusServerNodeRepository from "./repositories/octopusServerNodeRepository";
import PackageRepository from "./repositories/packageRepository";
import PerformanceConfigurationRepository from "./repositories/performanceConfigurationRepository";
import PermissionDescriptionRepository from "./repositories/permissionDescriptionRepository";
import ProgressionRepository from "./repositories/progressionRepository";
import ProjectGroupRepository from "./repositories/projectGroupRepository";
import ProjectRepository from "./repositories/projectRepository";
import ProjectTriggerRepository from "./repositories/projectTriggerRepository";
import ProxyRepository from "./repositories/proxyRepository";
import ReleasesRepository from "./repositories/releasesRepository";
import RetentionPolicyRepository from "./repositories/retentionPolicyRepository";
import SchedulerRepository from "./repositories/schedulerRepository";
import ServerConfigurationRepository from "./repositories/serverConfigurationRepository";
import ServerStatusRepository from "./repositories/serverStatusRepository";
import SmtpConfigurationRepository from "./repositories/smtpConfigurationRepository";
import SubscriptionRepository from "./repositories/subscriptionRepository";
import TagSetRepository from "./repositories/tagSetRepository";
import TaskRepository from "./repositories/taskRepository";
import TeamRepository from "./repositories/teamRepository";
import TenantRepository from "./repositories/tenantRepository";
import TenantVariableRepository from "./repositories/tenantVariableRepository";
import UpgradeConfigurationRepository from "./repositories/upgradeConfigurationRepository";
import UserIdentityMetadataRepository from "./repositories/userIdentityMetadataRepository";
import UserOnBoardingRepository from "./repositories/userOnBoardingRepository";
import UserRepository from "./repositories/userRepository";
import UserRoleRepository from "./repositories/userRoleRepository";
import VariableRepository from "./repositories/variableRepository";
import RootResource from "./resources/rootResource";
import WorkerPoolsRepository from "./repositories/workerpoolsRepository";
import WorkerRepository from "./repositories/workerRepository";

// Repositories provide a helpful abstraction around the Octopus Deploy API
class Repository {
    readonly takeAll = 2147483647;
    Accounts: AccountRepository;
    ActionTemplates: ActionTemplateRepository;
    Artifacts: ArtifactRepository;
    Authentication: AuthenticationRepository;
    Backups: BackupRepository;
    BuiltInRepositoryConfiguration: BuiltInRepositoryConfigurationRepository;
    CertificateConfiguration: CertificateConfigurationRepository;
    Certificates: CertificateRepository;
    Channels: ChannelRepository;
    CloudTemplates: CloudTemplateRepository;
    CommunityActionTemplates: CommunityActionTemplateRepository;
    DashboardConfiguration: DashboardConfigurationRepository;
    Dashboards: DashboardRepository;
    Defects: DefectRepository;
    Deployments: DeploymentRepository;
    DeploymentProcesses: DeploymentProcessRepository;
    Environments: EnvironmentRepository;
    Events: EventRepository;
    ExternalSecurityGroupProviders: ExternalSecurityGroupProviderRepository;
    ExternalSecurityGroups: ExternalSecurityGroupRepository;
    ExternalUsers: ExternalUsersRepository;
    FeaturesConfiguration: FeaturesConfigurationRepository;
    Feeds: FeedRepository;
    Interruptions: InterruptionRepository;
    LetsEncryptConfiguration: LetsEncryptConfigurationRepository;
    LibraryVariableSets: LibraryVariableRepository;
    Licenses: LicenseRepository;
    Lifecycles: LifecycleRepository;
    MachinePolicies: MachinePolicyRepository;
    MachineRoles: MachineRoleRepository;
    Machines: MachineRepository;
    MaintenanceConfiguration: MaintenanceConfigurationRepository;
    OctopusServerNodes: OctopusServerNodeRepository;
    Packages: PackageRepository;
    PerformanceConfiguration: PerformanceConfigurationRepository;
    PermissionDescriptions: PermissionDescriptionRepository;
    Progression: ProgressionRepository;
    ProjectGroups: ProjectGroupRepository;
    Projects: ProjectRepository;
    ProjectTriggers: ProjectTriggerRepository;
    Proxies: ProxyRepository;
    Releases: ReleasesRepository;
    resolve: (path: string, uriTemplateParameters?: any) => string;
    resolveLinkTemplate: (link: any, args: any) => string;
    RetentionPolicies: RetentionPolicyRepository;
    Scheduler: SchedulerRepository;
    ServerStatus: ServerStatusRepository;
    ServerConfiguration: ServerConfigurationRepository;
    Settings: SettingsRepository;
    SmtpConfiguration: SmtpConfigurationRepository;
    Subscriptions: SubscriptionRepository;
    TagSets: TagSetRepository;
    Tasks: TaskRepository;
    Teams: TeamRepository;
    Tenants: TenantRepository;
    TenantVariables: TenantVariableRepository;
    UpgradeConfiguration: UpgradeConfigurationRepository;
    UserIdentityMetadata: UserIdentityMetadataRepository;
    UserOnboarding: UserOnBoardingRepository;
    UserRoles: UserRoleRepository;
    Users: UserRepository;
    Variables: VariableRepository;
    WorkerPools: WorkerPoolsRepository;
    Workers: WorkerRepository;
    getRootDocument: () => RootResource;

    constructor(client: Client) {
        this.resolve = client.resolve;
        this.resolveLinkTemplate = client.resolveLinkTemplate;

        this.Accounts = new AccountRepository(client);
        this.ActionTemplates = new ActionTemplateRepository(client);
        this.Artifacts = new ArtifactRepository(client);
        this.Authentication = new AuthenticationRepository(client);
        this.Backups = new BackupRepository(client);
        this.BuiltInRepositoryConfiguration = new BuiltInRepositoryConfigurationRepository(client);
        this.CertificateConfiguration = new CertificateConfigurationRepository(client);
        this.Certificates = new CertificateRepository(client);
        this.Channels = new ChannelRepository(client);
        this.CloudTemplates = new CloudTemplateRepository(client);
        this.CommunityActionTemplates = new CommunityActionTemplateRepository(client);
        this.DashboardConfiguration = new DashboardConfigurationRepository(client);
        this.Dashboards = new DashboardRepository(client);
        this.Defects = new DefectRepository(client);
        this.Deployments = new DeploymentRepository(client);
        this.DeploymentProcesses = new DeploymentProcessRepository(client);
        this.Environments = new EnvironmentRepository(client);
        this.Events = new EventRepository(client);
        this.ExternalSecurityGroupProviders = new ExternalSecurityGroupProviderRepository(client);
        this.ExternalSecurityGroups = new ExternalSecurityGroupRepository(client);
        this.ExternalUsers = new ExternalUsersRepository(client);
        this.FeaturesConfiguration = new FeaturesConfigurationRepository(client);
        this.Feeds = new FeedRepository(client);
        this.Interruptions = new InterruptionRepository(client);
        this.LetsEncryptConfiguration = new LetsEncryptConfigurationRepository(client);
        this.LibraryVariableSets = new LibraryVariableRepository(client);
        this.Licenses = new LicenseRepository(client);
        this.Lifecycles = new LifecycleRepository(client);
        this.MachinePolicies = new MachinePolicyRepository(client);
        this.MachineRoles = new MachineRoleRepository(client);
        this.Machines = new MachineRepository(client);
        this.MaintenanceConfiguration = new MaintenanceConfigurationRepository(client);
        this.OctopusServerNodes = new OctopusServerNodeRepository(client);
        this.Packages = new PackageRepository(client);
        this.PerformanceConfiguration = new PerformanceConfigurationRepository(client);
        this.PermissionDescriptions = new PermissionDescriptionRepository(client);
        this.Progression = new ProgressionRepository(client);
        this.ProjectGroups = new ProjectGroupRepository(client);
        this.Projects = new ProjectRepository(client);
        this.ProjectTriggers = new ProjectTriggerRepository(client);
        this.Proxies = new ProxyRepository(client);
        this.Releases = new ReleasesRepository(client);
        this.RetentionPolicies = new RetentionPolicyRepository(client);
        this.Scheduler = new SchedulerRepository(client);
        this.ServerStatus = new ServerStatusRepository(client);
        this.ServerConfiguration = new ServerConfigurationRepository(client);
        this.Settings = new SettingsRepository(client);
        this.SmtpConfiguration = new SmtpConfigurationRepository(client);
        this.Subscriptions = new SubscriptionRepository(client);
        this.TagSets = new TagSetRepository(client);
        this.Tasks = new TaskRepository(client);
        this.Teams = new TeamRepository(client);
        this.Tenants = new TenantRepository(client);
        this.TenantVariables = new TenantVariableRepository(client);
        this.UpgradeConfiguration = new UpgradeConfigurationRepository(client);
        this.UserIdentityMetadata = new UserIdentityMetadataRepository(client);
        this.UserOnboarding = new UserOnBoardingRepository(client);
        this.UserRoles = new UserRoleRepository(client);
        this.Users = new UserRepository(client);
        this.Variables = new VariableRepository(client);
        this.WorkerPools = new WorkerPoolsRepository(client);
        this.Workers = new WorkerRepository(client);
        this.getRootDocument = client.getRootDocument.bind(client);
    }
}

export default Repository;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/client/repository.ts